<template>
  <main>

    <!-- Hero -->
    <div class="public-quiet-hero bg-zinc-900 py-20 px-6 text-center">
      <h1 class="text-4xl sm:text-5xl font-bold text-white mb-5">Häufig gestellte Fragen</h1>
      <p class="text-stone-300 text-xl max-w-2xl mx-auto">
        Du findest hier Antworten auf die häufigsten Fragen rund um unsere Veranstaltungstechnik.
        Ist noch etwas offen?
        <NuxtLink to="/contact" class="text-[#4C9CD4] underline hover:text-stone-300 transition-colors">Schreib uns einfach.</NuxtLink>
      </p>
    </div>

    <!-- Accordion -->
    <div class="public-quiet-section bg-zinc-800 py-16 px-6">
      <div class="max-w-3xl mx-auto faq-accordion">
        <div class="faq-groups">
          <section
            v-for="group in faqGroups"
            :key="group.slug"
            class="faq-group"
            :aria-labelledby="`faq-topic-${group.slug}`"
          >
            <h2 :id="`faq-topic-${group.slug}`" class="faq-topic">{{ group.title }}</h2>
            <div class="faq-list">
              <details v-for="item in group.items" :key="item.question" class="faq-item">
                <summary class="faq-trigger">
                  <span>{{ item.question }}</span>
                  <i class="pi pi-chevron-down faq-trigger__icon" aria-hidden="true"></i>
                </summary>
                <div class="faq-answer">
                  <p>{{ item.answer }}</p>
                </div>
              </details>
            </div>
          </section>
        </div>
      </div>
    </div>

    <!-- CTA -->
    <div class="public-quiet-section bg-zinc-900 py-16 px-6 text-center">
      <h2 class="text-3xl font-bold text-white mb-4">Noch eine offene Frage?</h2>
      <p class="text-stone-300 text-lg mb-8 max-w-xl mx-auto">
        Wir helfen dir gerne persönlich weiter.
      </p>
      <Button as="router-link" to="/contact" class="px-8">Kontakt aufnehmen</Button>
    </div>

  </main>
</template>

<script setup lang="ts">
const faqs = [
  {
    question: 'Was macht Mody Veranstaltungstechnik genau?',
    answer:
      'Wir bieten professionelle Veranstaltungstechnik für Events jeder Größe – von kleinen Feiern bis zu mittelgroßen Konzerten. Unser Schwerpunkt liegt auf Licht-, Ton- und Bühnentechnik zur Miete, inklusive Lieferung, Aufbau und Betreuung.'
  },
  {
    question: 'Betreut ihr auch Firmenfeiern, Vorträge oder Produktpräsentationen?',
    answer:
      'Ja. Wir begleiten neben Hochzeiten, Konzerten und Vereinsfesten auch Firmenfeiern, Vorträge, Produktpräsentationen und ähnliche Veranstaltungen mit passender Ton-, Licht- und Bühnentechnik.'
  },
  {
    question: 'Kann ich die Technik auch selbst abholen?',
    answer:
      'Ja, kleinere Sets und Geräte können nach Absprache selbst abgeholt werden. Für größere Veranstaltungen empfehlen wir den Aufbau durch unser Team.'
  },
  {
    question: 'Wo ist Mody Veranstaltungstechnik tätig?',
    answer:
      'Wir sind im Raum Pongau, Lungau und Salzburg aktiv, bis in die Steiermark – zum Beispiel in Flachau, Altenmarkt, Radstadt, Bischofshofen, Tamsweg, St. Michael im Lungau, Mauterndorf, Hallein, Schladming und Salzburg-Stadt.'
  },
  {
    question: 'Wie kann ich eine Anfrage stellen?',
    answer:
      'Einfach über unser Kontaktformular oder direkt über die Produktseiten im Shop. Wir erstellen dann ein individuelles Angebot, abgestimmt auf dein Event.'
  },
  {
    question: 'Wie lange im Voraus sollte ich buchen?',
    answer:
      'Je früher, desto besser. Für kleinere Sets reicht aber oft auch eine Woche Vorlauf.'
  },
  {
    question: 'Wie früh sollte man eine Firmenveranstaltung anfragen?',
    answer:
      'Am besten so früh wie möglich, damit Termin, Technik und Ablauf sauber abgestimmt werden können. Kleinere Formate gehen oft auch kurzfristiger, wenn der Termin noch frei ist.'
  },
  {
    question: 'Unterstützt ihr bei Planung, Aufbau und Betreuung vor Ort?',
    answer:
      'Ja. Wir stimmen Technik und Ablauf im Vorfeld mit euch ab, liefern das Equipment, bauen es auf und betreuen Veranstaltungen auf Wunsch auch direkt vor Ort.'
  },
  {
    question: 'Achtet ihr bei Vorträgen und Moderationen auf gute Sprachverständlichkeit?',
    answer:
      'Ja, gerade bei Vorträgen, Moderationen und Programmpunkten mit Sprache achten wir besonders auf saubere Mikrofonie, verständliche Beschallung und ein Setup, das zur Location passt.'
  },
  {
    question: 'Kann ich Equipment auch nur für einen Tag mieten?',
    answer:
      'Ja, Tagesmieten sind sogar der häufigste Fall. Bei längeren Mietzeiten erstellen wir dir gerne ein angepasstes Angebot.'
  },
  {
    question: 'Was passiert, wenn etwas kaputt geht?',
    answer:
      'Wir achten auf hochwertige Technik und sorgfältigen Aufbau. Sollte doch einmal etwas passieren, melde dich sofort – wir finden immer eine faire Lösung.'
  },
  {
    question: 'Bringt ihr die Technik vorbei?',
    answer:
      'Ja, Lieferung und Aufbau durch unser Team sind auf Wunsch möglich und werden im Angebot berücksichtigt.'
  },
  {
    question: 'Was ist der Unterschied zwischen kompletter Betreuung und Selbstabhol-Sets?',
    answer:
      'Bei kompletter Betreuung kümmern wir uns um Planung, Lieferung, Aufbau und auf Wunsch auch um die technische Begleitung vor Ort. Selbstabhol-Sets sind für kleinere Veranstaltungen gedacht, bei denen ihr die Technik selbst abholen und nutzen möchtet.'
  },
  {
    question: 'Welche Technik ist in den Sets enthalten?',
    answer:
      'Jedes Set ist sorgfältig zusammengestellt und enthält genau die Komponenten, die für den jeweiligen Einsatzzweck sinnvoll sind.'
  },
  {
    question: 'Wie funktioniert die Bezahlung?',
    answer:
      'Die Bezahlung erfolgt in der Regel per Überweisung.'
  }
]

const faqGroups = [
  {
    slug: 'leistungen',
    title: 'Leistungen & Einsatzgebiete',
    items: [faqs[0]!, faqs[1]!, faqs[3]!, faqs[7]!, faqs[8]!],
  },
  {
    slug: 'anfrage',
    title: 'Anfrage, Termin & Bezahlung',
    items: [faqs[4]!, faqs[5]!, faqs[6]!, faqs[9]!, faqs[14]!],
  },
  {
    slug: 'miete',
    title: 'Lieferung, Selbstabholung & Sets',
    items: [faqs[2]!, faqs[10]!, faqs[11]!, faqs[12]!, faqs[13]!],
  },
]

useSeoMeta({
  title: 'FAQ – Häufig gestellte Fragen',
  description:
    'Antworten auf häufig gestellte Fragen rund um den Verleih von Licht-, Ton- und Veranstaltungstechnik bei Mody Veranstaltungstechnik.'
})

useSchemaOrg([
   defineWebPage({
    '@type': 'FAQPage',
  }),
  ...faqs.map(f =>
    defineQuestion({
      name: f.question,
      acceptedAnswer: f.answer
    })
  )
])
</script>

<style scoped>
.faq-group + .faq-group {
  margin-top: 3rem;
}

.faq-topic {
  margin: 0 0 1rem;
  color: #fff;
  font-size: clamp(1.45rem, 3vw, 1.8rem);
  font-weight: 600;
  letter-spacing: -0.025em;
}

.faq-item {
  border-bottom: 1px solid rgb(63 63 70);
}

.faq-item:first-child {
  border-top: 1px solid rgb(63 63 70);
}

.faq-trigger {
  width: 100%;
  min-height: 56px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.25rem;
  padding: 18px 0;
  text-align: left;
  font-size: 16px;
  font-weight: 500;
  color: #f4f4f5;
  cursor: pointer;
  list-style: none;
}

.faq-trigger::-webkit-details-marker {
  display: none;
}

.faq-trigger:hover,
.faq-trigger:focus-visible {
  color: #fff;
}

.faq-trigger__icon {
  flex: 0 0 auto;
  color: #E39737;
  transition: transform 180ms ease;
}

.faq-item[open] .faq-trigger__icon {
  transform: rotate(180deg);
}

.faq-answer {
  font-size: 15px;
  color: rgb(168 162 158);
  line-height: 1.7;
  padding: 0 0 20px;
}

.faq-answer p {
  margin: 0;
}

@media (prefers-reduced-motion: reduce) {
  .faq-trigger__icon {
    transition: none;
  }
}
</style>
